import React from 'react';
import { useData } from '../../context/DataContext';
import { getIconWithFallback } from '../../utils/iconMap';
import '../../styles/pages/About.css';

const About = () => {
  const { data, getAbout, getUIText, loading, error } = useData();
  const about = getAbout();
  const aboutUI = getUIText('about');
  const backgroundImage = data?.images?.backgrounds?.about;

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (error) {
    return <div className="error-container"><p>Error loading data</p></div>;
  }

  const heroStyle = backgroundImage ? {
    '--page-hero-bg': `url(${backgroundImage})`
  } : {};

  return (
    <main className="about-page mandala-bg">
      <section className={`page-hero${backgroundImage ? ' has-bg-image' : ''}`} style={heroStyle}>
        <div className="container">
          <h1 className="page-title">{aboutUI?.pageTitle || 'About Us'}</h1>
          <p className="page-subtitle">{aboutUI?.pageSubtitle}</p>
        </div>
      </section>

      {/* Story Section */}
      <section className="section story-section">
        <div className="container">
          <div className="story-grid">
            <div className="story-content">
              <h2 className="section-title">{aboutUI?.storySection?.title || 'Our Origins'}</h2>
              <p className="story-summary">{about?.summary}</p>
              <p className="story-journey">{about?.journey}</p>
            </div>
            <div className="story-image">
              <img src={data?.images?.content?.about} alt={`About ${data?.name}`} />
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      {about?.coreValues?.length > 0 && (
        <section className="section values-section">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">{aboutUI?.valuesSection?.title || 'Our Values'}</h2>
              <p className="section-subtitle">{aboutUI?.valuesSection?.subtitle}</p>
            </div>
            <div className="values-grid">
              {about.coreValues.map((value, index) => (
                <div key={index} className="value-card">
                  <div className="value-icon">{getIconWithFallback(value.icon)}</div>
                  <h3 className="value-title">{value.title}</h3>
                  <p className="value-description">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Leadership Team */}
      {about?.leadershipTeam?.length > 0 && (
        <section className="section team-section">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">{aboutUI?.teamSection?.title || 'Our Team'}</h2>
              <p className="section-subtitle">{aboutUI?.teamSection?.subtitle}</p>
            </div>
            <div className="team-grid">
              {about.leadershipTeam.map((member, index) => (
                <div key={index} className="team-card">
                  <div className="team-photo">
                    <img src={member.photo || 'https://via.placeholder.com/200'} alt={member.name} />
                  </div>
                  <h3 className="team-name">{member.name}</h3>
                  <p className="team-position">{member.position}</p>
                  <p className="team-bio">{member.bio}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Certifications */}
      {about?.certifications?.length > 0 && (
        <section className="section certifications-section">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">Our Certifications</h2>
            </div>
            <div className="certifications-grid">
              {about.certifications.map((cert, index) => (
                <div key={index} className="cert-card">
                  <h3 className="cert-name">{cert.name}</h3>
                  <p className="cert-description">{cert.description}</p>
                  <p className="cert-issuer">Issued by: {cert.issuedBy}</p>
                  {cert.registrationNumber && (
                    <p className="cert-number">Reg: {cert.registrationNumber}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Legal Info */}
      {data?.legalInfo && (
        <section className="section legal-section">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">Legal Information</h2>
            </div>
            <div className="legal-grid">
              {data.legalInfo.businessType && (
                <div className="legal-item">
                  <span className="legal-label">Business Type</span>
                  <span className="legal-value">{data.legalInfo.businessType}</span>
                </div>
              )}
              {data.legalInfo.registrationNumber && (
                <div className="legal-item">
                  <span className="legal-label">Registration Number</span>
                  <span className="legal-value">{data.legalInfo.registrationNumber}</span>
                </div>
              )}
              {data.legalInfo.gstin && (
                <div className="legal-item">
                  <span className="legal-label">GSTIN</span>
                  <span className="legal-value">{data.legalInfo.gstin}</span>
                </div>
              )}
              {data.legalInfo.panNumber && (
                <div className="legal-item">
                  <span className="legal-label">PAN Number</span>
                  <span className="legal-value">{data.legalInfo.panNumber}</span>
                </div>
              )}
              {data.legalInfo.tanNumber && (
                <div className="legal-item">
                  <span className="legal-label">TAN Number</span>
                  <span className="legal-value">{data.legalInfo.tanNumber}</span>
                </div>
              )}
              {data.legalInfo.cinNumber && (
                <div className="legal-item">
                  <span className="legal-label">CIN Number</span>
                  <span className="legal-value">{data.legalInfo.cinNumber}</span>
                </div>
              )}
              {data.legalInfo.fssaiLicense && (
                <div className="legal-item">
                  <span className="legal-label">FSSAI License</span>
                  <span className="legal-value">{data.legalInfo.fssaiLicense}</span>
                </div>
              )}
              {data.legalInfo.udyamRegistration?.number && (
                <div className="legal-item">
                  <span className="legal-label">Udyam Registration</span>
                  <span className="legal-value">{data.legalInfo.udyamRegistration.number}</span>
                </div>
              )}
              {data.legalInfo.foundedYear && (
                <div className="legal-item">
                  <span className="legal-label">Founded</span>
                  <span className="legal-value">{data.legalInfo.foundedYear}</span>
                </div>
              )}
              {/* Custom Fields */}
              {data.legalInfo.customFields?.map((field, index) => (
                field.value && (
                  <div key={index} className="legal-item">
                    <span className="legal-label">{field.label}</span>
                    <span className="legal-value">{field.value}</span>
                  </div>
                )
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Banking Details */}
      {data?.bankingDetails && (data.bankingDetails.accountName || data.bankingDetails.bankName || data.bankingDetails.accountNumber) && (
        <section className="section banking-section">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">Banking Details</h2>
            </div>
            <div className="banking-grid">
              {data.bankingDetails.accountName && (
                <div className="banking-item">
                  <span className="banking-label">Account Name</span>
                  <span className="banking-value">{data.bankingDetails.accountName}</span>
                </div>
              )}
              {data.bankingDetails.bankName && (
                <div className="banking-item">
                  <span className="banking-label">Bank</span>
                  <span className="banking-value">{data.bankingDetails.bankName}</span>
                </div>
              )}
              {data.bankingDetails.accountNumber && (
                <div className="banking-item">
                  <span className="banking-label">Account Number</span>
                  <span className="banking-value">{data.bankingDetails.accountNumber}</span>
                </div>
              )}
              {data.bankingDetails.ifscCode && (
                <div className="banking-item">
                  <span className="banking-label">IFSC Code</span>
                  <span className="banking-value">{data.bankingDetails.ifscCode}</span>
                </div>
              )}
              {data.bankingDetails.branchName && (
                <div className="banking-item">
                  <span className="banking-label">Branch</span>
                  <span className="banking-value">{data.bankingDetails.branchName}</span>
                </div>
              )}
            </div>
          </div>
        </section>
      )}
    </main>
  );
};

export default About;
